import React from 'react';
import styled from '@emotion/styled';
import { Rocket, Map, Target, AlertTriangle, Lightbulb, TreePine, ArrowDown, Search } from 'lucide-react';
import KuutioCubeWebGL from '../KuutioCubeWebGL';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  overflow: hidden;

  html[data-platform='tizen'] & {
    background: #12121a;
  }
`;

const Inner = styled.div`
  display: flex;
  align-items: center;
  max-width: 900px;
`;

const CubeSection = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 24px;
`;

const Divider = styled.div`
  width: 1px;
  height: 60%;
  background: linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.1) 70%, transparent 100%);
  flex-shrink: 0;

  html[data-platform='tizen'] & {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 32px 40px;
  gap: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
  max-width: 440px;
`;

const JourneyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
  max-width: 420px;
`;

const JourneyCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
`;

const JourneyIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => p.$bg};
  color: ${p => p.$color};
`;

const JourneyLabel = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.35);
`;

const JourneyValue = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
`;

const StartButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 36px;
  background: #2E7BC4;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #367ABA;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(46, 123, 196, 0.3);
  }
`;

export default function LensStartView({
  onStart,
  scenarioCount = 0,
  pivotPointCount = 0,
  nodeCount = 0,
  perspectiveTitle = '',
}) {
  const { t } = useTranslation();
  return (
    <Container>
      <Inner>
      <CubeSection>
        <KuutioCubeWebGL size={240} duration={25} />
      </CubeSection>

      <Divider />

      <ContentSection>
        <Title>{t('lensComponents.startView.title')}</Title>
        <Subtitle>
          {t('lensComponents.startView.subtitle')}
        </Subtitle>

        {/* Flow: stats → arrow → goal */}
        <JourneyGrid>
          {pivotPointCount > 0 && (
            <JourneyCard>
              <JourneyIcon $bg="rgba(76, 175, 80, 0.15)" $color="#4CAF50">
                <Target size={18} />
              </JourneyIcon>
              <div>
                <JourneyValue>{pivotPointCount}</JourneyValue>
                <JourneyLabel>{t('lensComponents.startView.labelPivot')}</JourneyLabel>
              </div>
            </JourneyCard>
          )}

          <JourneyCard>
            <JourneyIcon $bg="rgba(255, 170, 0, 0.12)" $color="#ffaa00">
              <Lightbulb size={18} />
            </JourneyIcon>
            <div>
              <JourneyValue>6</JourneyValue>
              <JourneyLabel>{t('lensComponents.startView.labelLens')}</JourneyLabel>
            </div>
          </JourneyCard>
        </JourneyGrid>

        <div style={{ display: 'flex', justifyContent: 'center', width: '100%', maxWidth: 420, color: 'rgba(255,255,255,0.15)' }}>
          <ArrowDown size={20} />
        </div>

        <JourneyCard style={{ width: '100%', maxWidth: 420, background: 'rgba(147, 51, 234, 0.06)', borderColor: 'rgba(147, 51, 234, 0.2)' }}>
          <JourneyIcon $bg="rgba(147, 51, 234, 0.15)" $color="#c084fc">
            <Search size={18} />
          </JourneyIcon>
          <div>
            <JourneyValue style={{ fontSize: '1rem' }}>{t('lensComponents.startView.goalTitle')}</JourneyValue>
            <JourneyLabel>{t('lensComponents.startView.goalSubtitle')}</JourneyLabel>
          </div>
        </JourneyCard>

        <StartButton onClick={onStart}>
          <Rocket size={20} />
          {t('lensComponents.startView.cta')}
        </StartButton>
      </ContentSection>
      </Inner>
    </Container>
  );
}
